/**
 * ContentOverridesContext — loads admin-set text overrides from Supabase
 * on app boot and makes them available to the I18nProvider, which merges
 * them on top of the static CONTENT tree so every useI18n() consumer
 * automatically sees the overridden values.
 *
 * Provider hierarchy (see main.tsx):
 *   <ContentOverridesProvider>     ← this file
 *     <I18nProvider>               ← reads overrides from this context
 *       <AuthProvider>
 *         <App />
 *       </AuthProvider>
 *     </I18nProvider>
 *   </ContentOverridesProvider>
 *
 * Shape of `overrides`: { [lang]: { [dot.key]: value } }
 *   e.g. { de: { "hero.title": "Mein Titel" }, en: { "hero.title": "My Title" } }
 *
 * The `reload` function is exposed so the admin TextEditor can re-fetch
 * after a save without a full page reload.
 */

import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useState,
  type ReactNode,
} from "react";
import {
  loadAllOverrides,
  type ContentOverride,
} from "../lib/content-overrides";

/** Per-language flat key→value override map. */
export type OverridesMap = Record<string, Record<string, string>>;

type Ctx = {
  /** All loaded overrides, keyed by lang then content_key. */
  overrides: OverridesMap;
  /** Raw rows from Supabase — used by admin TextEditor for display/editing. */
  rawRows: ContentOverride[];
  /** Re-fetches all overrides from Supabase. Call after admin saves. */
  reload: () => Promise<void>;
  /** True once the first load has completed (even if it returned 0 rows). */
  loaded: boolean;
};

const ContentOverridesContext = createContext<Ctx | null>(null);

function rowsToMap(rows: ContentOverride[]): OverridesMap {
  const map: OverridesMap = {};
  for (const row of rows) {
    if (!map[row.lang]) map[row.lang] = {};
    map[row.lang][row.content_key] = row.value;
  }
  return map;
}

export function ContentOverridesProvider({ children }: { children: ReactNode }) {
  const [rawRows, setRawRows] = useState<ContentOverride[]>([]);
  const [overrides, setOverrides] = useState<OverridesMap>({});
  const [loaded, setLoaded] = useState(false);

  const reload = useCallback(async () => {
    const rows = await loadAllOverrides();
    setRawRows(rows);
    setOverrides(rowsToMap(rows));
    setLoaded(true);
  }, []);

  useEffect(() => {
    reload();
  }, [reload]);

  return (
    <ContentOverridesContext.Provider value={{ overrides, rawRows, reload, loaded }}>
      {children}
    </ContentOverridesContext.Provider>
  );
}

export function useContentOverrides(): Ctx {
  const ctx = useContext(ContentOverridesContext);
  if (!ctx) {
    throw new Error(
      "useContentOverrides must be used within ContentOverridesProvider"
    );
  }
  return ctx;
}
